import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../services/api";
import { Button, Card, Field, Input, PageHeader, RoleIdList, Select } from "../components/ui";
import { AnalyticsPage } from "./AnalyticsPage";
import { useUi } from "../stores/ui";
import { can } from "../types";
import { useState } from "react";

export function AdminDashboardPage() {
  const user = useUi((s) => s.user);
  const queryClient = useQueryClient();
  const health = useQuery({
    queryKey: ["health-admin"],
    queryFn: () =>
      api<{
        discord: { connected: boolean; lastPingAt: string | null };
        lastSyncAt: string | null;
        lastSyncMemberCount: number;
        lastSyncError: string | null;
        mongo: boolean;
        webhook: { configured: boolean; pending: number; failed: number };
        sockets: number;
        scheduler: { jobs: { name: string; pattern: string }[] };
        version: string;
      }>("/api/admin/health"),
    enabled: can(user, "MANAGE_SETTINGS"),
  });
  const sync = useMutation({
    mutationFn: () => api("/api/admin/discord/sync", { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries(),
    meta: { success: "Discord roster synced" },
  });
  const recalc = useMutation({
    mutationFn: () => api("/api/admin/stats/recalculate", { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries(),
    meta: { success: "Statistics recalculated" },
  });

  return (
    <div>
      <PageHeader
        title="Admin Dashboard"
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
            {can(user, "SYNC_DISCORD") ? <Button onClick={() => sync.mutate()}>Sync Discord</Button> : null}
            {can(user, "RECALCULATE_STATS") ? <Button variant="outline" onClick={() => recalc.mutate()}>Recalculate Statistics</Button> : null}
            {can(user, "EXPORT_REPORTS") ? <a href="/api/admin/export" className="sm:inline-flex"><Button variant="ghost" className="w-full sm:w-auto">Export Report</Button></a> : null}
            <Link to="/admin/settings"><Button variant="ghost" className="w-full sm:w-auto">Manage Requirements</Button></Link>
          </div>
        }
      />
      {health.data ? (
        <div className="mb-6 grid gap-3 md:grid-cols-4">
          <Card>
            <p className="text-sm text-zinc-400">Discord Bot</p>
            <p className="font-semibold">{health.data.discord.connected ? "Connected (REST)" : "Unavailable"}</p>
          </Card>
          <Card>
            <p className="text-sm text-zinc-400">Last Sync</p>
            <p className="font-semibold">{health.data.lastSyncAt ? new Date(health.data.lastSyncAt).toLocaleString() : "Never"}</p>
            <p className="text-xs text-zinc-500">{health.data.lastSyncMemberCount} HR members synced</p>
          </Card>
          <Card>
            <p className="text-sm text-zinc-400">Sync Errors</p>
            <p className="font-semibold">{health.data.lastSyncError ?? "0"}</p>
          </Card>
          <Card>
            <p className="text-sm text-zinc-400">System</p>
            <p className="text-sm">MongoDB {health.data.mongo ? "connected" : "down"}</p>
            <p className="text-sm">Sockets {health.data.sockets}</p>
            <p className="text-sm">Version {health.data.version}</p>
          </Card>
        </div>
      ) : null}
      <AnalyticsPage />
    </div>
  );
}

export function AdminsPanel() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["managers"],
    queryFn: () =>
      api<{ managers: { discordId: string; role: string; displayName: string; protected: boolean }[] }>("/api/admin/managers"),
  });
  const [discordId, setDiscordId] = useState("");
  const add = useMutation({
    mutationFn: () => api("/api/admin/managers", { method: "POST", body: JSON.stringify({ discordId: discordId.trim() }) }),
    onSuccess: () => {
      setDiscordId("");
      void queryClient.invalidateQueries({ queryKey: ["managers"] });
    },
    meta: { success: "Admin added", successBody: "They can sign in with Discord to use admin access." },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/admin/managers/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["managers"] }),
    meta: { success: "Admin removed" },
  });

  return (
    <Card className="space-y-4">
      <h3 className="text-sm font-semibold text-white">Admins</h3>
      <p className="text-xs leading-relaxed text-zinc-400">
        Add a Discord user ID. They get admin access to the Hub, but they cannot add or remove other admins.
      </p>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
        <Field label="Discord user ID" hint="Right-click the user in Discord with Developer Mode on, then Copy User ID.">
          <Input
            inputMode="numeric"
            placeholder="999033836358873260"
            value={discordId}
            onChange={(e) => setDiscordId(e.target.value)}
            autoComplete="off"
          />
        </Field>
        <div className="flex items-end">
          <Button className="w-full sm:w-auto" disabled={!discordId.trim() || add.isPending} onClick={() => add.mutate()}>
            Add admin
          </Button>
        </div>
      </div>
      <div className="divide-y divide-white/5 border-t border-white/10">
        {(data?.managers ?? []).map((item) => (
          <div key={item.discordId} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="truncate font-semibold">{item.displayName}</p>
              <p className="truncate text-xs text-zinc-500">
                {item.discordId} • {item.protected || item.role === "SUPER_ADMIN" ? "Owner" : "Admin"}
              </p>
            </div>
            <Button
              variant="danger"
              disabled={item.protected || item.role === "SUPER_ADMIN" || remove.isPending}
              onClick={() => remove.mutate(item.discordId)}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ManagersPage() {
  return (
    <div>
      <PageHeader title="Admins" subtitle="Grant hub admin access by Discord user ID." />
      <AdminsPanel />
    </div>
  );
}

export function AuditPage() {
  const [action, setAction] = useState("");
  const { data } = useQuery({
    queryKey: ["audit", action],
    queryFn: () => api<{ items: { _id: string; action: string; actorDiscordId: string; targetDiscordId: string; timestamp: string }[] }>(`/api/admin/audit?action=${action}`),
  });
  return (
    <div>
      <PageHeader title="Audit Logs" subtitle="Immutable application history." />
      <Input className="mb-4 w-full max-w-sm" placeholder="Filter action" value={action} onChange={(e) => setAction(e.target.value)} />
      <Card>
        <div className="hidden md:block">
          {(data?.items ?? []).map((item) => (
            <div key={item._id} className="grid grid-cols-4 gap-2 border-t border-white/5 py-2 text-sm first:border-0">
              <span>{item.action}</span>
              <span className="truncate text-zinc-500">{item.actorDiscordId}</span>
              <span className="truncate text-zinc-500">{item.targetDiscordId}</span>
              <span>{new Date(item.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
        <div className="space-y-3 md:hidden">
          {(data?.items ?? []).map((item) => (
            <div key={item._id} className="rounded-md border border-white/10 p-3 text-sm">
              <p className="font-medium">{item.action}</p>
              <p className="mt-1 text-xs text-zinc-500">Actor {item.actorDiscordId}</p>
              <p className="text-xs text-zinc-500">Target {item.targetDiscordId}</p>
              <p className="mt-1 text-xs text-zinc-400">{new Date(item.timestamp).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export function SettingsPage() {
  const user = useUi((s) => s.user);
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: () => api<{ settings: Record<string, unknown> }>("/api/admin/settings"),
  });
  const [form, setForm] = useState<Record<string, unknown> | null>(null);
  const settings = form ?? data?.settings ?? {};
  const save = useMutation({
    mutationFn: () => api("/api/admin/settings", { method: "PATCH", body: JSON.stringify(settings) }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
    meta: { success: "Settings saved" },
  });
  function set<K extends string>(key: K, value: unknown) {
    setForm({ ...settings, [key]: value });
  }
  function roleIds(key: "interviewTeamRoleIds" | "trainingTeamRoleIds", fallback: "interviewTeamRoleId" | "trainingTeamRoleId") {
    const list = settings[key];
    if (Array.isArray(list) && list.length) return list.map((item) => String(item));
    const one = String(settings[fallback] ?? "");
    return one ? [one] : [""];
  }
  return (
    <div>
      <PageHeader title="Settings" subtitle="Hub configuration. Bot tokens are never shown here." />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="space-y-4">
          <h3 className="text-sm font-semibold text-white">General</h3>
          <Field label="Hub name" hint="Shown in the browser title area and login screen.">
            <Input value={String(settings.hubName ?? "")} onChange={(e) => set("hubName", e.target.value)} />
          </Field>
          <Field label="Display timezone" hint="IANA timezone used for timestamps in the interface, for example America/New_York.">
            <Input value={String(settings.timezone ?? "")} onChange={(e) => set("timezone", e.target.value)} />
          </Field>
          <Field label="Discord server ID" hint="The guild this HR Hub belongs to.">
            <Input value={String(settings.guildId ?? "")} onChange={(e) => set("guildId", e.target.value)} />
          </Field>
          <Field label="HR member role ID" hint="Anyone with this Discord role is treated as an HR member.">
            <Input value={String(settings.hrRoleId ?? "")} onChange={(e) => set("hrRoleId", e.target.value)} />
          </Field>
          <RoleIdList
            label="Interview team role IDs"
            hint="Members with any of these Discord roles are placed on Interview Team. They only need the interview requirement unless they also have a training role."
            values={roleIds("interviewTeamRoleIds", "interviewTeamRoleId")}
            onChange={(ids) =>
              setForm({
                ...settings,
                interviewTeamRoleIds: ids,
                interviewTeamRoleId: ids.find((id) => id.trim()) ?? "",
              })
            }
          />
          <RoleIdList
            label="Training team role IDs"
            hint="Members with any of these Discord roles are placed on Training Team. They only need the training requirement unless they also have an interview role."
            values={roleIds("trainingTeamRoleIds", "trainingTeamRoleId")}
            onChange={(ids) =>
              setForm({
                ...settings,
                trainingTeamRoleIds: ids,
                trainingTeamRoleId: ids.find((id) => id.trim()) ?? "",
              })
            }
          />
        </Card>
        <Card className="space-y-4">
          <h3 className="text-sm font-semibold text-white">Monthly activity requirements</h3>
          <Field label="Interviews required" hint="Monthly interviews required for Interview Team members.">
            <Input type="number" min={0} value={Number(settings.interviewRequirement ?? 5)} onChange={(e) => set("interviewRequirement", Number(e.target.value))} />
          </Field>
          <Field label="Trainings required" hint="Monthly trainings required for Training Team members.">
            <Input type="number" min={0} value={Number(settings.trainingRequirement ?? 2)} onChange={(e) => set("trainingRequirement", Number(e.target.value))} />
          </Field>
          <p className="rounded-md border border-white/10 bg-ink-950 px-3 py-2 text-xs leading-relaxed text-zinc-400">
            Interview-only members must meet interviews. Training-only members must meet trainings. Members with both roles must meet both numbers.
          </p>
          <Field label="Reporting timezone" hint="Month boundaries are calculated in this timezone, not each member’s local clock.">
            <Input value={String(settings.reportingTimezone ?? "")} onChange={(e) => set("reportingTimezone", e.target.value)} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input className="mt-1" type="checkbox" checked={Boolean(settings.allowMultipleTeams)} onChange={(e) => set("allowMultipleTeams", e.target.checked)} />
            <span>
              Allow both Interview and Training at once
              <span className="mt-0.5 block text-xs text-zinc-500">If on, a member with both Discord roles is on both teams and must meet both requirements.</span>
            </span>
          </label>
        </Card>
        <Card className="space-y-4">
          <h3 className="text-sm font-semibold text-white">Activity logging</h3>
          <Field label="Maximum quantity per submission" hint="Slider range when someone logs activity. Typically 10.">
            <Input type="number" min={1} max={50} value={Number(settings.maxQuantityPerSubmission ?? 10)} onChange={(e) => set("maxQuantityPerSubmission", Number(e.target.value))} />
          </Field>
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input className="mt-1" type="checkbox" checked={Boolean(settings.notesRequired)} onChange={(e) => set("notesRequired", e.target.checked)} />
            <span>Require a note on every submission</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input className="mt-1" type="checkbox" checked={Boolean(settings.allowBackdating)} onChange={(e) => set("allowBackdating", e.target.checked)} />
            <span>Allow dating activity in the past</span>
          </label>
        </Card>
        <Card className="space-y-4">
          <h3 className="text-sm font-semibold text-white">Discord</h3>
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input className="mt-1" type="checkbox" checked={Boolean(settings.webhookLoggingEnabled)} onChange={(e) => set("webhookLoggingEnabled", e.target.checked)} />
            <span>
              Send action logs to Discord
              <span className="mt-0.5 block text-xs text-zinc-500">Activity, roster, and settings changes post to the webhook below.</span>
            </span>
          </label>
          <Field
            label="Logging webhook URL"
            hint="Paste a Discord channel webhook. This is where HR logs are sent."
          >
            <Input
              type="url"
              placeholder="https://discord.com/api/webhooks/..."
              value={String(settings.webhookUrl ?? "")}
              onChange={(e) => set("webhookUrl", e.target.value)}
              autoComplete="off"
            />
          </Field>
          <label className="flex items-start gap-2 text-sm text-zinc-300">
            <input className="mt-1" type="checkbox" checked={Boolean(settings.roleSyncEnabled)} onChange={(e) => set("roleSyncEnabled", e.target.checked)} />
            <span>
              Automatically add and remove roster members when Discord roles change
              <span className="mt-0.5 block text-xs text-zinc-500">
                HR role add/remove updates the roster within about 20 seconds. Interview and Training roles rematch teams the same way. The bot needs View Audit Log.
              </span>
            </span>
          </label>
          <Field label="Full sync backup (minutes)" hint="Safety pass that re-checks the whole guild. Live role changes do not wait for this. Allowed range: 1–30.">
            <Input type="number" min={1} max={30} value={Number(settings.syncIntervalMinutes ?? 5)} onChange={(e) => set("syncIntervalMinutes", Number(e.target.value))} />
          </Field>
          <p className="text-xs text-zinc-500">
            {settings.webhookConfigured || String(settings.webhookUrl ?? "").trim()
              ? "Webhook is ready to send logs."
              : "No webhook URL saved yet. Logs will stay in the Hub until you add one."}
          </p>
          <Field label="Leaderboards">
            <Select value={String(settings.leaderboardVisibility ?? "PUBLIC")} onChange={(e) => set("leaderboardVisibility", e.target.value)}>
              <option value="PUBLIC">Visible to HR members</option>
              <option value="ADMIN_ONLY">Admin only</option>
              <option value="DISABLED">Disabled</option>
            </Select>
          </Field>
        </Card>
        {can(user, "MANAGE_MANAGERS") ? (
          <div className="lg:col-span-2">
            <AdminsPanel />
          </div>
        ) : null}
      </div>
      <Button className="mt-4" onClick={() => save.mutate()}>Save settings</Button>
    </div>
  );
}
